/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import {
  autoUpdate,
  Composite,
  CompositeItem,
  flip,
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useListItem,
  useListNavigation,
  useMergeRefs,
  useTransitionStyles,
} from '@floating-ui/react';
import { tokens } from '@neo4j-ndl/base';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useId, useRef, useState } from 'react';

import { IconIndicatorWrapper } from '../_common/IconIndicatorWrapper';
import {
  type CommonProps,
  type PolymorphicCommonProps,
} from '../_common/types';
import { CleanIconButton } from '../clean-icon-button';
import { ConditionalWrap } from '../conditional-wrap';
import { Divider } from '../divider';
import { ChevronRightIconOutline, PinIcon } from '../icons';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import {
  SideNavCategoryContext,
  SideNavContext,
  useSideNavCategoryContext,
  useSideNavContext,
} from './side-navigation-context';
import {
  type CategoryHeaderProps,
  type CategoryItemProps,
  type ItemBadgeProps,
  type NavItemProps,
  type SideNavListItemProps,
  type SideNavProps,
} from './side-navigation-types';

/**
 * SideNav
 */
const SideNavComponent = ({
  children,
  className,
  style,
  htmlAttributes,
  isExpanded = false,
  shouldOverlayOnInteraction = true,
  ariaLabel,
  onPinButtonClick,
  expandedWidth,
  ref,
  ...restProps
}: CommonProps<'div', SideNavProps>) => {
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocusVisible, setHasFocusVisible] = useState(false);
  const [isCollapseTransitioning, setIsCollapseTransitioning] = useState(false);
  const collapseTransitionTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);

  const isFloatingExpanded =
    shouldOverlayOnInteraction && (!isHovered ? hasFocusVisible : true);

  const classes = classNames('ndl-side-nav', 'ndl-side-nav-root', className, {
    'ndl-side-nav-collapsed':
      isExpanded !== true && shouldOverlayOnInteraction !== true,
    'ndl-side-nav-expanded': isExpanded,
  });

  const innerClasses = classNames('ndl-side-nav-inner', {
    'ndl-side-nav-collapsing': isCollapseTransitioning,
    'ndl-side-nav-hover': isFloatingExpanded === true && isExpanded !== true,
  });

  const customStyle = {
    ...style,
    ...(Boolean(expandedWidth) && {
      '--side-nav-width-expanded':
        typeof expandedWidth === 'string'
          ? expandedWidth
          : `${expandedWidth}px`,
    }),
  };

  const mergedRef = useMergeRefs([ref, rootRef]);

  const collapseTransitionDuration = Number.parseInt(
    tokens.motion.duration.quick,
    10,
  );

  useEffect(() => {
    return () => {
      if (collapseTransitionTimeoutRef.current !== null) {
        clearTimeout(collapseTransitionTimeoutRef.current);
        collapseTransitionTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <SideNavContext.Provider
      value={{
        isExpanded,
        isHovered: isFloatingExpanded, // needs refactoring
        onPinButtonClick,
        shouldOverlayOnInteraction,
      }}
    >
      <div
        className={classes}
        ref={mergedRef}
        style={customStyle}
        onMouseEnter={() => {
          if (collapseTransitionTimeoutRef.current !== null) {
            clearTimeout(collapseTransitionTimeoutRef.current);
            collapseTransitionTimeoutRef.current = null;
          }
          setIsCollapseTransitioning(false);
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          const shouldDelayZIndexRemoval =
            shouldOverlayOnInteraction &&
            !isExpanded &&
            (isHovered || hasFocusVisible);
          setIsHovered(false);
          if (shouldDelayZIndexRemoval) {
            if (collapseTransitionTimeoutRef.current !== null) {
              clearTimeout(collapseTransitionTimeoutRef.current);
            }
            setIsCollapseTransitioning(true);
            collapseTransitionTimeoutRef.current = setTimeout(() => {
              collapseTransitionTimeoutRef.current = null;
              setIsCollapseTransitioning(false);
            }, collapseTransitionDuration);
          }
        }}
        onFocusCapture={() => {
          // Delay slightly to let Composite's focus management settle
          requestAnimationFrame(() => {
            const activeElement = document.activeElement;
            // Check if focus is inside and visible (using :focus-visible on the active element)
            if (
              activeElement instanceof HTMLElement &&
              rootRef.current !== null &&
              rootRef.current.contains(activeElement) &&
              activeElement.matches(':focus-visible')
            ) {
              // Update state after another frame to let focus fully settle
              // This updates the context for consumers without disrupting focus
              requestAnimationFrame(() => {
                setHasFocusVisible(true);
              });
            }
          });
        }}
        onBlurCapture={() => {
          // Small delay to check if focus moved to another element inside
          requestAnimationFrame(() => {
            const activeElement = document.activeElement;
            const isInsideNav =
              activeElement instanceof HTMLElement &&
              rootRef.current !== null &&
              rootRef.current.contains(activeElement);
            // Check if focus is in a portaled floating menu
            const isInsideFloatingMenu =
              activeElement instanceof HTMLElement &&
              activeElement.closest('.ndl-side-nav-popover') !== null;

            if (
              (isInsideNav || isInsideFloatingMenu) &&
              activeElement instanceof HTMLElement &&
              activeElement.matches(':focus-visible')
            ) {
              // Focus stayed inside with focus-visible
              setHasFocusVisible(true);
            } else {
              // Focus left - collapse
              setHasFocusVisible(false);
            }
          });
        }}
        {...restProps}
        {...htmlAttributes}
      >
        <div className={innerClasses}>
          {/** TODO v5: should the nav be the one that gets all the stuff spread on it? */}
          <nav className="ndl-side-nav-nav" aria-label={ariaLabel}>
            <Composite
              as="ul"
              aria-label={ariaLabel}
              role="menubar"
              orientation="vertical"
              aria-orientation="vertical"
              className="ndl-side-nav-list"
              loop={true}
            >
              {children}
            </Composite>
          </nav>
          {shouldOverlayOnInteraction === true && (
            <div className="ndl-side-nav-footer">
              <PinButton />
            </div>
          )}
        </div>
      </div>
    </SideNavContext.Provider>
  );
};

/**
 * SideNavListItem
 */
const SideNavListItem = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'li', SideNavListItemProps>) => {
  const classes = classNames('ndl-side-nav-list-item', className, {});

  return (
    <li
      role="none"
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      <SelectedIndicator />
      {children}
    </li>
  );
};

/**
 * NavItem
 */
const NavItem = <T extends React.ElementType = 'button'>({
  icon,
  label,
  trailingElement,
  isActive,
  className,
  style,
  htmlAttributes,
  as,
  badge,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, NavItemProps>) => {
  const { isExpanded, isHovered, shouldOverlayOnInteraction } =
    useSideNavContext();
  const categoryContext = useSideNavCategoryContext();
  const isInCategory = categoryContext !== null;

  const item = useListItem();
  const mergedRef = useMergeRefs([item.ref, ref]);

  const isActiveIndex = item.index === categoryContext?.activeIndex;

  const Component: React.ElementType = as ?? 'button';
  const classes = classNames('ndl-side-nav-nav-item', className, {
    'ndl-active': isActive === true,
  });

  const Wrapper = isInCategory ? Component : CompositeItem;
  const componentProps = {
    className: classes,
    ref: mergedRef,
    role: 'menuitem',
    style: style,
    ...(isInCategory && {
      ...categoryContext?.getItemProps(),
      tabIndex: isActiveIndex ? 0 : -1,
    }),
    ...restProps,
    ...htmlAttributes,
  };
  const wrapperProps = isInCategory
    ? componentProps
    : {
        render: <Component {...componentProps} />,
      };

  return (
    <ConditionalWrap
      shouldWrap={
        isExpanded === false &&
        !shouldOverlayOnInteraction &&
        !isInCategory &&
        Boolean(label)
      }
      wrap={(children) => (
        <Tooltip
          type="simple"
          placement="right"
          hoverDelay={{ close: 0, open: shouldOverlayOnInteraction ? 100 : 0 }}
        >
          <Tooltip.Trigger hasButtonWrapper={true}>{children}</Tooltip.Trigger>
          <Tooltip.Content>{label}</Tooltip.Content>
        </Tooltip>
      )}
    >
      <Wrapper {...wrapperProps}>
        <div className="ndl-side-nav-item-inner">
          {Boolean(icon) && (
            <div className="ndl-side-nav-item-leading-element">
              <ConditionalWrap
                shouldWrap={Boolean(badge) && !isHovered && !isExpanded}
                wrap={(children) => (
                  <IconIndicatorWrapper type={badge?.type ?? 'info'}>
                    {children}
                  </IconIndicatorWrapper>
                )}
              >
                {icon}
              </ConditionalWrap>
            </div>
          )}
          {Boolean(label) && (
            <Typography
              variant="body-medium"
              as="div"
              className="ndl-side-nav-item-label"
            >
              {label}
            </Typography>
          )}
          {(Boolean(trailingElement) || Boolean(badge)) && (
            <div className="ndl-side-nav-item-trailing-element">
              {trailingElement}
              {badge !== undefined && (
                <ItemBadge type={badge.type} number={badge.number} />
              )}
            </div>
          )}
        </div>
      </Wrapper>
    </ConditionalWrap>
  );
};

/**
 * CategoryItem
 */
const CategoryItem = ({
  children,
  icon,
  label,
  isActive,
  className,
  style,
  htmlAttributes,
  badge,
  isMenuOpen = false,
  ref,
  ...restProps
}: CommonProps<'button', CategoryItemProps>) => {
  const floatingId = useId();
  const { isExpanded, isHovered, shouldOverlayOnInteraction } =
    useSideNavContext();
  const anchorRef = useRef<HTMLLIElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const elementsRef = useRef<
    Array<HTMLButtonElement | HTMLAnchorElement | null>
  >([]);

  const classes = classNames('ndl-side-nav-category-item', className, {
    'ndl-active': isActive === true,
  });

  // Floating UI setup
  const { refs, context } = useFloating({
    elements: {
      reference: anchorRef.current,
    },
    middleware: [
      offset(5),
      flip({ crossAxis: true, fallbackAxisSideDirection: 'end', padding: 5 }),
      shift({ padding: 5 }),
    ],
    onOpenChange: setIsOpen,
    open: isOpen || isMenuOpen,
    placement: 'right-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context, {
    referencePress: false,
  });
  const hover = useHover(context, {
    delay: { close: 130, open: 130 },
    handleClose: safePolygon({
      blockPointerEvents: false,
      requireIntent: false,
    }),
  });
  const focus = useFocus(context);
  const listNavigation = useListNavigation(context, {
    activeIndex: activeIndex,
    focusItemOnHover: false,
    focusItemOnOpen: false,
    listRef: elementsRef,
    loop: true,
    nested: true,
    onNavigate: setActiveIndex,
    openOnArrowKeyDown: false,
  });

  // Separate interactions: reference (no listNavigation), floating (with listNavigation)
  const refInteractions = useInteractions([dismiss, hover, focus]);
  const floatInteractions = useInteractions([dismiss, hover, listNavigation]);
  const { styles: transitionStyles } = useTransitionStyles(context, {
    duration: Number.parseInt(tokens.motion.duration.quick) ?? 0,
  });

  // Reset activeIndex when menu closes
  useEffect(() => {
    if (!context.open) {
      setActiveIndex(null);
    }
  }, [context.open]);

  const mergedRef = useMergeRefs([refs.setReference, ref]);

  return (
    <SideNavCategoryContext.Provider
      value={{
        activeIndex,
        getItemProps: floatInteractions.getItemProps,
      }}
    >
      <ConditionalWrap
        shouldWrap={
          isExpanded === false && !shouldOverlayOnInteraction && Boolean(label)
        }
        wrap={(children) => (
          <Tooltip
            type="simple"
            placement="top"
            hoverDelay={{
              close: 0,
              open: shouldOverlayOnInteraction ? 100 : 0,
            }}
            isOpen={isTooltipOpen}
            onOpenChange={setIsTooltipOpen}
          >
            <Tooltip.Trigger hasButtonWrapper={true}>
              {children}
            </Tooltip.Trigger>
            <Tooltip.Content className="ndl-side-nav ndl-side-nav-category-menu-tooltip-content">
              {label}
            </Tooltip.Content>
          </Tooltip>
        )}
      >
        <CompositeItem
          render={
            <button
              type="button"
              {...refInteractions.getReferenceProps({
                className: classes,
                ref: mergedRef,
                style,
                ...restProps,
                ...htmlAttributes,
              })}
              // {...restProps}
              // {...htmlAttributes}
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={context.open}
              aria-controls={floatingId}
              onFocus={(e) => {
                // Only show tooltip on focus-visible (keyboard navigation)
                if (
                  e.target instanceof HTMLElement &&
                  e.target.matches(':focus-visible')
                ) {
                  setIsTooltipOpen(true);
                }
                // Call original handler if exists
                const originalOnFocus = refInteractions.getReferenceProps({})
                  .onFocus as
                  | ((e: React.FocusEvent<HTMLButtonElement>) => void)
                  | undefined;
                originalOnFocus?.(e);
              }}
              onBlur={(e) => {
                setIsTooltipOpen(false);
                // Call original handler if exists
                const originalOnBlur = refInteractions.getReferenceProps({})
                  .onBlur as
                  | ((e: React.FocusEvent<HTMLButtonElement>) => void)
                  | undefined;
                originalOnBlur?.(e);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') {
                  // open and move focus into submenu
                  setIsOpen(true);
                  setActiveIndex(0);
                  // Defer focusing first item (if needed) to next tick
                  requestAnimationFrame(() => {
                    const el = elementsRef.current[0];
                    el?.focus();
                  });
                  e.preventDefault();
                }
                // ArrowDown/ArrowUp: let Composite handle top-level roving focus.
              }}
            />
          }
        >
          <div className="ndl-side-nav-item-inner">
            {Boolean(icon) && (
              <div className="ndl-side-nav-item-leading-element">
                <ConditionalWrap
                  shouldWrap={Boolean(badge) && !isHovered && !isExpanded}
                  wrap={(children) => (
                    <IconIndicatorWrapper type={badge?.type ?? 'info'}>
                      {children}
                    </IconIndicatorWrapper>
                  )}
                >
                  {icon}
                </ConditionalWrap>
              </div>
            )}
            {Boolean(label) && (
              <Typography
                variant="body-medium"
                as="div"
                className="ndl-side-nav-item-label"
              >
                {label}
              </Typography>
            )}
            <div className="ndl-side-nav-item-trailing-element">
              {badge && <ItemBadge type={badge.type} number={badge.number} />}
              <ChevronRightIconOutline className="n-size-4 n-text-neutral-icon" />
            </div>
          </div>
          {context.open && (
            <FloatingList elementsRef={elementsRef}>
              <FloatingPortal>
                <FloatingFocusManager
                  context={context}
                  modal={false}
                  initialFocus={-1}
                  returnFocus={true}
                  closeOnFocusOut={true}
                  guards={true}
                >
                  <ul
                    id={floatingId}
                    ref={refs.setFloating}
                    className="ndl-side-nav ndl-side-nav-popover ndl-side-nav-popover-list"
                    style={{
                      ...context.floatingStyles,
                      ...transitionStyles,
                    }}
                    {...floatInteractions.getFloatingProps()}
                    role="menu"
                  >
                    {children}
                  </ul>
                </FloatingFocusManager>
              </FloatingPortal>
            </FloatingList>
          )}
        </CompositeItem>
      </ConditionalWrap>
    </SideNavCategoryContext.Provider>
  );
};

/**
 * CategoryHeader
 */
const CategoryHeader = ({
  children,
  className,
  ref,
  style,
  htmlAttributes,
  ...restProps
}: CommonProps<'li', CategoryHeaderProps>) => {
  const { isExpanded, isHovered } = useSideNavContext();

  const classes = classNames('ndl-side-nav-category-header', className, {
    'ndl-side-nav-category-header-expanded': isExpanded || isHovered,
  });

  return (
    <Typography
      ref={ref}
      variant="label"
      as="li"
      className={classes}
      htmlAttributes={{
        ...htmlAttributes,
        role: 'separator',
      }}
      style={style}
      {...restProps}
    >
      {isExpanded || isHovered ? children : <Divider />}
    </Typography>
  );
};

/**
 * SelectedIndicator
 */
const SelectedIndicator = () => {
  return <div className="ndl-side-nav-selected-indicator" />;
};

/**
 * SideNavDivider
 */
const SideNavDivider = () => {
  return (
    <Divider
      orientation="horizontal"
      className="ndl-side-nav-divider"
      as="li"
    />
  );
};

/**
 * ItemBadge
 */
const ItemBadge = ({
  number,
  type,
  ref,
  htmlAttributes,
  style,
  className,
  ...restProps
}: CommonProps<'span', ItemBadgeProps>) => {
  useSideNavContext();

  const badgeClasses = classNames('ndl-side-nav-item-badge', className, {
    'ndl-danger': type === 'danger',
    'ndl-info': type === 'info',
    'ndl-warning': type === 'warning',
  });

  return (
    <Typography
      as="span"
      className={badgeClasses}
      variant="subheading-small"
      htmlAttributes={{
        'aria-label': `${number} notifications of type ${type}`,
        ...htmlAttributes,
      }}
      style={style}
      ref={ref}
      {...restProps}
    >
      {number}
    </Typography>
  );
};

/**
 * PinButton
 */
const PinButton = () => {
  const { isExpanded, shouldOverlayOnInteraction, onPinButtonClick } =
    useSideNavContext();

  const classes = classNames('ndl-side-nav-pin-button');

  if (!shouldOverlayOnInteraction) {
    return null;
  }

  return (
    <CleanIconButton
      onClick={onPinButtonClick}
      className={classes}
      description="Pin"
      size="medium"
      isActive={isExpanded}
      tooltipProps={{
        root: {
          autoUpdateOptions: {
            animationFrame: true,
          },
          placement: 'right',
        },
      }}
    >
      <PinIcon />
    </CleanIconButton>
  );
};

const SideNavigation = Object.assign(SideNavComponent, {
  CategoryHeader: CategoryHeader,
  CategoryItem: CategoryItem,
  Divider: SideNavDivider,
  ItemBadge: ItemBadge,
  ListItem: SideNavListItem,
  NavItem,
});

export { SideNavigation };
